// dashboardShared.tsx — morceaux communs aux onglets du Dashboard refaits le 2026-10-17
//
// Les onglets Vidéos, Assets, Enregistrements et Trafic reprennent la grammaire de
// l'onglet Chaînes (maquette https://claude.ai/artifact/MtJ8i9W8i9G2uHH3CcnL7N) :
// chiffres du haut, barre d'outils, le plus récent en grand, cartes par jour, vue
// liste en bascule. Ce qui se répète d'un onglet à l'autre vit ici ; les styles
// communs restent ceux de ChannelsSection.module.scss.

import React, { useEffect, useRef, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';
import { OPEN_CHANNEL_EVENT, OpenChannelDetail } from './ChannelsSection';

export type View = 'cards' | 'list';

// ── Point désigné sur la carte ───────────────────────────────────────────────

// Éléments d'un point cliqué sur la carte (ActivityTab `pickItems`) : surlignés dans les
// cartes comme dans la liste, le premier amené à l'écran. Même rendu que l'onglet Chaînes.
const PICKED    : React.CSSProperties = { outline: '2px solid #5D97F1', outlineOffset: 2 };
const PICKED_ROW: React.CSSProperties = { background: 'rgba(44,125,225,0.22)', boxShadow: 'inset 4px 0 0 #2C7DE1' };

export function usePicked<T>(picked: T[] | undefined, idOf: (x: T) => React.Key) {
  const ids = new Set((picked ?? []).map(idOf));
  const ref = useRef<HTMLDivElement | null>(null);
  const sig = Array.from(ids).join(',');
  useEffect(() => {
    if (sig) ref.current?.querySelector('[data-picked]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [sig]);
  /** Attributs à poser sur la carte (ou la ligne, `row`) d'un élément désigné. */
  const mark = (x: T, row = false) => ids.has(idOf(x)) ? { 'data-picked': true, style: row ? PICKED_ROW : PICKED } : {};
  return { ref, mark };
}

// ── Textes ───────────────────────────────────────────────────────────────────

export const initials = (name: string) =>
  name.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';

export const ownerText = (owner: string, email: string) =>
  owner && email && owner !== email ? owner : (owner || email || 'Créateur inconnu');

export const hour = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

export const dayLabel = (iso: string | null | undefined): string => {
  if (!iso) return 'Date inconnue';
  const today  = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(iso); target.setHours(0, 0, 0, 0);
  const diff = Math.round((today.getTime() - target.getTime()) / 86_400_000);
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Hier';
  const label = target.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const ago = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 24 * 60) return `il y a ${Math.round(minutes / 60)} h`;
  return dayLabel(iso).toLowerCase();
};

/** 0:47, 12:48, 1:12:04 — comme un lecteur vidéo. */
export const clock = (sec: number | null | undefined): string => {
  if (sec == null || !isFinite(sec)) return '';
  const t = Math.max(0, Math.round(sec)), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), r = t % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
};

/** 8 min, 1 h 12 — pour une durée qu'on lit plutôt qu'on ne compare. */
export const spoken = (sec: number | null | undefined): string => {
  if (sec == null || !isFinite(sec)) return '';
  const m = Math.max(0, Math.round(sec / 60));
  return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`;
};

/** Convention binaire du projet (1 Go = 1024³ octets), comme les quotas. */
export const size = (bytes: number | null | undefined): string => {
  if (bytes == null || !isFinite(bytes) || bytes <= 0) return '';
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toLocaleString('fr-FR', { maximumFractionDigits: i < 2 ? 0 : 1 })} ${units[i]}`;
};

/** Un nombre et son unité séparés, pour les gros chiffres (« 3,4 » « Go »). */
export const sizeParts = (bytes: number): [string, string] => {
  const s = size(bytes);
  if (!s) return ['0', 'o'];
  const i = s.lastIndexOf(' ');
  return [s.slice(0, i), s.slice(i + 1)];
};

/** Ville (et pays) de la connexion du créateur qui a précédé la création (champ `geo`
 *  d'admin-api, CreatorWhereabouts) ; '' quand on ne la connaît pas. */
export const creatorPlace = (geo: { city: string | null; country: string | null } | null | undefined) =>
  geo ? [geo.city, geo.country].filter(Boolean).join(', ') : '';
export const CREATOR_PLACE_HINT = "D'après la dernière connexion du créateur avant la création";

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n > 1 ? many : one}`;

// Couleur de repli d'une vignette : stable pour un même identifiant.
const TINTS = [ '#1E1B4B', '#164E63', '#7C2D12', '#3B0764', '#1E3A8A', '#831843', '#14532D', '#713F12' ];
export const tintOf = (id: number) => TINTS[Math.abs(id) % TINTS.length];

// ── Regroupement par jour ────────────────────────────────────────────────────

export function groupByDay<T>(items: T[], dateOf: (x: T) => string | null | undefined): { title: string; items: T[] }[] {
  const out: { title: string; items: T[] }[] = [];
  for (const x of items) {
    const title = dayLabel(dateOf(x));
    const g = out.find(y => y.title === title) ?? (out.push({ title, items: [] }), out[out.length - 1]);
    g.items.push(x);
  }
  return out;
}

// ── Navigation ───────────────────────────────────────────────────────────────

export const openChannel = (id: number, tab: OpenChannelDetail['tab']) =>
  window.dispatchEvent(new CustomEvent<OpenChannelDetail>(OPEN_CHANNEL_EVENT, { detail: { id, tab } }));

/** Ancre d'une chaîne dans la liste de l'onglet Chaînes (carte, vignette ou ligne). */
export const channelAnchorId = (id: number) => `dash-channel-${id}`;

/**
 * Fait défiler la page jusqu'à la chaîne dans la liste et la fait briller un instant
 * (2026-10-23). Faux si elle n'y est pas affichée (filtre d'origine ou d'état, tranche
 * du graphe figée) : l'appelant choisit alors quoi faire.
 */
export const scrollToChannel = (id: number): boolean => {
  const el = document.getElementById(channelAnchorId(id));
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.animate?.(
    [ { boxShadow: '0 0 0 3px #5D97F1, 0 0 24px rgba(93,151,241,0.6)' }, { boxShadow: '0 0 0 0 rgba(93,151,241,0)' } ],
    { duration: 1800, delay: 350, easing: 'ease-out' },
  );
  return true;
};

// ── Composants ───────────────────────────────────────────────────────────────

/** Bascule cartes / liste, retenue d'une visite à l'autre sous `storageKey`. */
export const useView = (storageKey: string): [View, (v: View) => void] => {
  // Vue en liste par défaut (2026-10-23) ; les cartes seulement si on les a choisies.
  const [ view, setView ] = useState<View>(() => {
    try { return localStorage.getItem(storageKey) === 'cards' ? 'cards' : 'list'; } catch { return 'list'; }
  });
  return [ view, (next: View) => {
    setView(next);
    try { localStorage.setItem(storageKey, next); } catch { /* navigation privée */ }
  } ];
};

export const ViewToggle: React.FC<{ view: View; onChange: (v: View) => void }> = ({ view, onChange }) => (
  <div role="group" aria-label="Affichage" className={styles.segment}>
    <button type="button" aria-label="Vue en cartes" aria-pressed={view === 'cards'} className={view === 'cards' ? styles.segOn : undefined} onClick={() => onChange('cards')}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
    </button>
    <button type="button" aria-label="Vue en liste" aria-pressed={view === 'list'} className={view === 'list' ? styles.segOn : undefined} onClick={() => onChange('list')}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/></svg>
    </button>
  </div>
);

export function Segment<K extends string>({ label, value, choices, onChange }:
  { label: string; value: K; choices: readonly (readonly [K, string])[]; onChange: (k: K) => void }) {
  return (
    <div role="group" aria-label={label} className={styles.segment}>
      {choices.map(([k, text]) => (
        <button key={k} type="button" aria-pressed={value === k} className={value === k ? styles.segOn : undefined} onClick={() => onChange(k)}>{text}</button>
      ))}
    </div>
  );
}

export const Owner: React.FC<{ owner: string; email: string; small?: boolean }> = ({ owner, email, small }) => {
  const name = ownerText(owner, email);
  return small
    ? <div className={styles.owner}>
        <span className={styles.avatarSmall}>{initials(name)}</span>
        <span className={styles.ownerSmall} title={email}>{name}</span>
      </div>
    : <div className={styles.owner}>
        <span className={styles.avatar}>{initials(name)}</span>
        <div className={styles.ownerText}>
          <span className={styles.ownerName}>{name}</span>
          {email && email !== name && <span className={styles.muted}>{email}</span>}
        </div>
      </div>;
};

export const Spark: React.FC<{ values: { key: string; value: number; title: string }[] }> = ({ values }) => {
  const max = Math.max(1, ...values.map(v => v.value));
  return (
    <div className={styles.spark} aria-hidden="true">
      {values.map((v, i) => <span key={v.key} className={i === values.length - 1 ? styles.sparkToday : undefined}
                                  style={{ height: `${Math.max(3, Math.round(v.value / max * 22))}px` }} title={v.title} />)}
    </div>
  );
};

export const EmptyState: React.FC<{ loading: boolean; title: string; hint: string; canClear: boolean; onClear: () => void }> =
  ({ loading, title, hint, canClear, onClear }) => (
  <div className={styles.emptyState}>
    <div className={styles.emptyText}>
      <strong>{loading ? 'Chargement…' : title}</strong>
      {!loading && <span>{hint}</span>}
    </div>
    {!loading && canClear && <button type="button" className={styles.secondary} onClick={onClear}>Effacer les filtres</button>}
  </div>
);

/** Le jour de chaque barre d'un relevé « 7 jours », pour l'infobulle. */
export const shortDay = (ymd: string) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });

// ── Plage de dates personnalisée ─────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');
/** « 2026-10-24T19:42 », la valeur d'un champ datetime-local (heure locale). */
const ymdhm = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fromYmdhm = (s: string) => {
  const [ day, time = '' ] = s.split('T');
  const [ y, m, d ] = day.split('-').map(Number);
  const [ h = 0, mi = 0 ] = time.split(':').map(Number);
  return y && m && d ? new Date(y, m - 1, d, h, mi) : null;
};

/** Bornes par défaut de « Personnaliser » : aujourd'hui, de 00:00 à 23:59. */
export const todayStart = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 0, 0); };
export const todayEnd   = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 23, 59); };

/** Plage « Personnaliser » à la minute près : du début de sa minute à la fin de la sienne. */
export const customRange = (from: Date, to: Date): [Date, Date] => [
  new Date(from.getFullYear(), from.getMonth(), from.getDate(), from.getHours(), from.getMinutes(), 0, 0),
  new Date(to.getFullYear(), to.getMonth(), to.getDate(), to.getHours(), to.getMinutes(), 59, 999),
];

/** « 3 jours 4 h », « 2 h 15 min », « 45 min » : la durée de la plage, sur la pastille. */
const spanText = (ms: number) => {
  const min = Math.round(ms / 60_000);
  const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
  if (d > 0) return `${plural(d, 'jour')}${h ? ` ${h} h` : ''}`;
  if (h > 0) return `${h} h${m ? ` ${pad(m)} min` : ''}`;
  return `${m} min`;
};

/**
 * « Personnaliser » de la barre Période (2026-10-22) : une seule pastille sur une ligne,
 * « 📅 du [12/10/2026 08:00] → au [22/10/2026 18:30] · 10 jours 10 h ». À la minute près
 * depuis le 2026-10-24 (avant : au jour). Sélecteur natif du navigateur ; le début ne
 * dépasse pas la fin, la fin pas aujourd'hui 23:59.
 */
export const DateRange: React.FC<{ from: Date; to: Date; onFrom: (d: Date) => void; onTo: (d: Date) => void }> = ({ from, to, onFrom, onTo }) => {
  const [ start, end ] = customRange(from, to);
  const span = end.getTime() + 1 - start.getTime();
  return (
    <div className={tabs.dateRange} role="group" aria-label="Période personnalisée">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18M8 2v4M16 2v4"/>
      </svg>
      <label><span>du</span>
        <input type="datetime-local" step={60} value={ymdhm(from)} max={ymdhm(to)} onChange={e => { const d = fromYmdhm(e.target.value); if (d) onFrom(d); }} />
      </label>
      <span className={tabs.dateArrow} aria-hidden="true">→</span>
      <label><span>au</span>
        <input type="datetime-local" step={60} value={ymdhm(to)} min={ymdhm(from)} max={ymdhm(todayEnd())} onChange={e => { const d = fromYmdhm(e.target.value); if (d) onTo(d); }} />
      </label>
      {span > 0 && <span className={tabs.dateDays}>{spanText(span)}</span>}
    </div>
  );
};
